/**
 * API client for the call cost report (`crm-calls/cost.routes.ts`).
 *
 * Its own file beside `callCoverage.ts`, for the same reason that one is not
 * part of `calls.ts`: one read, no writes, and nothing on this screen edits a
 * call.
 */
import { api } from "./api";

/**
 * Twilio charges in one currency.
 *
 * A list rather than a number, because Twilio bills an account in whatever
 * currency it was set up with and adding two of them together would be wrong in
 * a way nobody would notice. On the ordinary single-currency account there is
 * exactly one entry and it reads as a number anyway.
 */
export interface MoneyBucket {
  unit: string;
  /** Millionths of `unit`. A minute to an Estonian mobile is about 8500. */
  micros: number;
}

export interface AiCost {
  tokensIn: number;
  tokensOut: number;
  /**
   * Millionths of a USD, or null when nothing here could be priced - an unknown
   * model id, or no tokens at all. Null is not zero, and the screen says so
   * differently: "cost nothing" and "nobody has priced this model" send a reader
   * to two different places.
   */
  micros: number | null;
  /** Calls whose tokens are on a model the server has no rate for. */
  unpricedCalls: number;
}

export interface CostSlice {
  calls: number;
  /** Talk time in seconds. Excludes ringing - it is `durationSec` summed. */
  seconds: number;
  money: MoneyBucket[];
  /**
   * Calls with no price yet. Twilio publishes one minutes after a call ends, so
   * on any recent period this is not zero, and hiding it would turn "so far" into
   * a claim about the whole month.
   */
  unpricedCalls: number;
  ai: AiCost;
}

export interface CostOperator extends CostSlice {
  userId: string;
  /** Null on the unclaimed inbound bucket, which has no operator to name. */
  name: string | null;
}

export interface CostReport extends CostSlice {
  from: string;
  to: string;
  /** The period held more calls than one report may read; the OLDEST are missing. */
  truncated: boolean;
  /** The model the AI figure was priced at, so the screen can name it. */
  model: string;
  operators: CostOperator[];
}

export function getCallCost(from: Date, to: Date): Promise<CostReport> {
  const q = new URLSearchParams({ from: from.toISOString(), to: to.toISOString() });
  return api.get(`/admin/crm/calls/cost?${q.toString()}`);
}
